import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getBanners, getJobs, getChatMessages, getVideoBanners, getCountries, getImageUrl } from '../services/api';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './Home.css';
import '../components/Filters.css'; // Use shared filter styles
import Flag from 'react-flagpack';
import 'react-flagpack/dist/style.css';

// Country flag mapping
const getCountryFlagCode = (code) => {
  if (!code) return null;
  const lowerCode = code.toLowerCase();

  // Special name-to-code overrides
  const overrides = {
    en: 'US',
    gb: 'GB',
    uae: 'AE',
    us: 'US',
    usa: 'US',
  };

  if (overrides[lowerCode]) return overrides[lowerCode];

  // If it's a standard 2-letter ISO code, Flagpack will handle it
  if (code.length === 2) return code.toUpperCase();

  return null;
};

// Text overrides for banners - map by image filename or banner ID
const BANNER_TEXT_OVERRIDES = {
  // Map by image filename (from PREVIEW_PICTURE_SRC)
  'rf41jlzqcqkgw0jdo95lpb9vugw2yler.png': {
    ZAG_COLOR: 'Топ туры <span>от BUYME</span>'
  },
  // You can add more overrides here by image filename or banner ID
  // Example: 'banner-id-123': { ZAG_COLOR: 'Your text here' }
};

// Helper function to decode HTML entities
const decodeHtmlEntities = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
};

// Helper function to get banner text with override support
const getBannerText = (banner, propertyCode) => {
  // First check for override by image filename
  const imageFilename = banner.PREVIEW_PICTURE_SRC?.split('/').pop();
  if (imageFilename && BANNER_TEXT_OVERRIDES[imageFilename] && BANNER_TEXT_OVERRIDES[imageFilename][propertyCode]) {
    return BANNER_TEXT_OVERRIDES[imageFilename][propertyCode];
  }

  // Check for override by banner ID
  if (BANNER_TEXT_OVERRIDES[banner.ID] && BANNER_TEXT_OVERRIDES[banner.ID][propertyCode]) {
    return BANNER_TEXT_OVERRIDES[banner.ID][propertyCode];
  }

  // Otherwise, try to get from properties
  const propValue = banner.PROPERTIES?.[propertyCode];
  if (!propValue) return '';

  // If it's still PHP serialized (starts with a:), return empty or try to extract
  if (typeof propValue.VALUE === 'string' && propValue.VALUE.startsWith('a:')) {
    // Try simple extraction
    const match = propValue.VALUE.match(/"TEXT";s:\d+:"([^"]+)";/);
    if (match && match[1]) {
      return decodeHtmlEntities(match[1]);
    }
    return ''; // Return empty if we can't parse
  }

  // Return the text value and decode HTML entities
  const text = propValue['~VALUE']?.TEXT || propValue.VALUE || '';
  return decodeHtmlEntities(text);
};

// Helper function to clean text for display (decodes HTML entities)
const cleanText = (text) => {
  if (!text) return '';
  return decodeHtmlEntities(String(text));
};

const Home = ({ language }) => {
  const [banners, setBanners] = useState([]);
  const [jobs, setJobs] = useState([]);

  const [chatMessages, setChatMessages] = useState([]);
  const [videoBanners, setVideoBanners] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCountryCode, setSelectedCountryCode] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null); // New Category State

  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState(null);
  const workBlockRef = useRef(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  // UI States for Premium Filter
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');

  useEffect(() => {
    AOS.init();
    loadData();

    // Auto-refresh chat messages every 5 seconds
    const chatInterval = setInterval(() => {
      loadChatMessages();
    }, 5000);

    return () => clearInterval(chatInterval);
  }, [language]);

  // Carousel auto-rotation for mobile banners - include all 3 banners
  useEffect(() => {
    const allBanners = banners.slice(0, 3); // Include all 3 banners (big + 2 small)
    if (allBanners.length <= 1) return;

    const carouselInterval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % allBanners.length);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(carouselInterval);
  }, [banners]);

  const loadChatMessages = async () => {
    try {
      const chatRes = await getChatMessages();
      const newMessages = chatRes.data || [];
      setChatMessages(newMessages);

      // Auto-scroll to bottom when new messages arrive (only if user is near bottom)
      setTimeout(() => {
        const chatContainer = document.getElementById('chatContainer');
        if (chatContainer) {
          const isNearBottom = chatContainer.scrollHeight - chatContainer.scrollTop <= chatContainer.clientHeight + 100;
          if (isNearBottom) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }
      }, 100);
    } catch (error) {
      console.error('Error refreshing chat messages:', error);
    }
  };

  // Generalized fetch function for Country + Category
  const fetchFilteredJobs = async (countryCode, category) => {
    try {
      setJobsLoading(true);
      setJobsError(null);

      const options = { limit: '5' };
      if (category) options.category = category;

      const jobsRes = await getJobs(countryCode || language, options);
      setJobs(jobsRes.data?.slice(0, 5) || []);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setJobsError(err.response?.data?.error || err.message || 'Failed to load jobs');
    } finally {
      setJobsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [bannersRes, jobsRes, chatRes, videoRes, countriesRes] = await Promise.all([
        getBanners(language),
        getJobs('all', { limit: 'all' }), // Fetch ALL jobs regardless of language/country
        getChatMessages(),
        getVideoBanners(language),
        getCountries()
      ]);

      setBanners(bannersRes.data || []);
      setJobs(jobsRes.data?.slice(0, 5) || []);
      setSelectedCountryCode(null);
      setSelectedCategory(null);
      setJobsError(null);
      setJobsLoading(false);

      const chatData = chatRes.data || [];
      setChatMessages(chatData);

      // Scroll chat to bottom on initial load
      setTimeout(() => {
        const chatContainer = document.getElementById('chatContainer');
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      }, 200);

      const videoBannersData = videoRes.data || [];


      setVideoBanners(videoBannersData);
      setCountries(countriesRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setError(error.response?.data?.error || error.message || 'Failed to load data. Please check if backend is running and database is set up.');
    } finally {
      setLoading(false);
    }
  };

  const handleCountryClick = async (country) => {
    if (!country?.CODE_COUNTRY) {
      return;
    }

    // Avoid refetch if already selected and not filtering
    if (selectedCountryCode === country.CODE_COUNTRY && !jobsLoading) {
      if (workBlockRef.current) {
        workBlockRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    setSelectedCountryCode(country.CODE_COUNTRY);
    await fetchFilteredJobs(country.CODE_COUNTRY, selectedCategory);

    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('selectedCountryCode', country.CODE_COUNTRY);
      } catch (storageError) {
        console.warn('Unable to persist country selection:', storageError);
      }
    }

    if (workBlockRef.current) {
      workBlockRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCategoryClick = async (catId) => {
    if (selectedCategory === catId) {
      setSelectedCategory(null);
      await fetchFilteredJobs(selectedCountryCode, null);
    } else {
      setSelectedCategory(catId);
      await fetchFilteredJobs(selectedCountryCode, catId);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedCode = localStorage.getItem('selectedCountryCode');
      if (savedCode) {
        setSelectedCountryCode(savedCode);
        fetchFilteredJobs(savedCode, null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const translations = {
    ru: {
      detail: 'Подробнее',
      all: 'Показать все',
      work: 'работа',

      t3add: 'хочешь найти красивую модель',
      t2add: 'для обложки журнала',
      conadd: 'Оставь контактные данные и заполни форму',
      subadd: 'Подать объявление',
      popthemes: 'Популярные темы',
      filt: 'Фильтр по странам',
      countryname: 'Страны',

      // Categories
      cat_all: 'Все',
      agency: 'Агентства',
      model: 'Модели',
      operator: 'Операторы',
      scout: 'Скауты'
    },
    en: {
      detail: 'More',
      all: 'Show all',
      work: 'job',

      t3add: 'Do you want to find a model',
      t2add: 'for the cover of a magazine',
      conadd: 'Leave your contact details and fill out the form',
      subadd: 'Submit an ad',
      popthemes: 'Popular topics',
      filt: 'Filter by country',
      countryname: 'Countries',

      // Categories
      cat_all: 'All',
      agency: 'Agencies',
      model: 'Models',
      operator: 'Operators',
      scout: 'Scouts'
    },
    it: {
      detail: 'More',
      all: 'Show all',
      work: 'lavoro',

      t3add: 'vuoi trovare un bel modello',
      t2add: 'per la copertina della rivista',
      conadd: 'Lascia i dettagli di contatto e compila il modulo',
      subadd: 'Invia un annuncio',
      popthemes: 'Argomenti popolari',
      filt: 'Filtra per Paese',
      countryname: 'Countries',

      // Categories
      cat_all: 'Tutti',
      agency: 'Agenzie',
      model: 'Modelli',
      operator: 'Operatori',
      scout: 'Scout'
    },
    fr: {
      detail: 'More',
      all: 'Show all',
      work: 'travail',

      t3add: 'envie de trouver un joli modèle',
      t2add: 'pour la couverture du magazine',
      conadd: 'Laissez les coordonnées et remplissez le formulaire',
      subadd: 'Soumettre une annonce',
      popthemes: 'Sujets populaires',
      filt: 'Filtrer par pays',
      countryname: 'Countries',

      // Categories
      cat_all: 'Tous',
      agency: 'Agences',
      model: 'Modèles',
      operator: 'Opérateurs',
      scout: 'Scouts'
    }
  };

  const t = translations[language] || translations.en;

  // Categories config
  const categories = [
    { id: 'agency', label: t.agency },
    { id: 'model', label: t.model },
    { id: 'operator', label: t.operator },
    { id: 'scout', label: t.scout }
  ];

  const filteredCountries = countries.filter(c =>
    c.NAME.toLowerCase().includes(countrySearchQuery.toLowerCase())
  );

  const activeCountry = countries.find(c => c.CODE_COUNTRY === selectedCountryCode);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="main">
      {/* Banners Section */}
      <div className="banner">
        <div className="row first">
          {/* Desktop: Show big banner separately, small banners stacked */}
          {banners[0] && (
            <div className="bann big desktop-big-banner" key={banners[0].ID}>
              <div className="image">
                <img src={getImageUrl(banners[0].PREVIEW_PICTURE_SRC)} alt="banner" fetchpriority="high" />
              </div>
              <div className="bann_cont">
                <h1 dangerouslySetInnerHTML={{ __html: getBannerText(banners[0], 'ZAG_COLOR') }} />
                {banners[0].PROPERTIES?.SPIS_USL?.VALUE && (
                  <div className="spisok">
                    <ul>
                      {(Array.isArray(banners[0].PROPERTIES.SPIS_USL.VALUE)
                        ? banners[0].PROPERTIES.SPIS_USL.VALUE
                        : [banners[0].PROPERTIES.SPIS_USL.VALUE]
                      ).map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="button">
                  <a href={banners[0].PROPERTIES?.LINK_OBJ?.VALUE || '#'}>{t.detail}</a>
                </div>
              </div>
            </div>
          )}

          {banners.length > 1 && (
            <div className="bann small desktop-small-banners">
              {banners.slice(1, 3).map((banner) => (
                <div key={banner.ID} className="smallest">
                  <div className="image">
                    <img src={getImageUrl(banner.PREVIEW_PICTURE_SRC)} alt="banner" loading="lazy" />
                  </div>
                  <div className="ban">
                    <div className="cent">
                      <h4>{banner.NAME}</h4>
                      {banner.PROPERTIES?.PRICE?.VALUE && (
                        <p
                          className="cost"
                          dangerouslySetInnerHTML={{ __html: cleanText(banner.PROPERTIES.PRICE.VALUE) }}
                        />
                      )}
                      <div className="button">
                        <a href={banner.PROPERTIES?.LINK_OBJ?.VALUE || '#'}>{t.detail}</a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Mobile: Carousel with all 3 banners */}
          {banners.length > 0 && (
            <div className="bann banner-carousel mobile-carousel">
              {banners.slice(0, 3).map((banner, index) => {
                const isBigBanner = index === 0;
                return (
                  <div
                    key={banner.ID}
                    className={`carousel-slide ${isBigBanner ? 'big' : 'smallest'} ${index === currentBannerIndex ? 'active' : ''}`}
                  >
                    <div className="image">
                      <img src={getImageUrl(banner.PREVIEW_PICTURE_SRC)} alt="banner" fetchpriority={index === 0 ? "high" : "auto"} loading={index === 0 ? "eager" : "lazy"} />
                    </div>
                    {isBigBanner ? (
                      <div className="bann_cont">
                        <h1 dangerouslySetInnerHTML={{ __html: getBannerText(banner, 'ZAG_COLOR') }} />
                        {banner.PROPERTIES?.SPIS_USL?.VALUE && (
                          <div className="spisok">
                            <ul>
                              {(Array.isArray(banner.PROPERTIES.SPIS_USL.VALUE)
                                ? banner.PROPERTIES.SPIS_USL.VALUE
                                : [banner.PROPERTIES.SPIS_USL.VALUE]
                              ).map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="button">
                          <a href={banner.PROPERTIES?.LINK_OBJ?.VALUE || '#'}>{t.detail}</a>
                        </div>
                      </div>
                    ) : (
                      <div className="ban">
                        <div className="cent">
                          <h4>{banner.NAME}</h4>
                          {banner.PROPERTIES?.PRICE?.VALUE && (
                            <p
                              className="cost"
                              dangerouslySetInnerHTML={{ __html: cleanText(banner.PROPERTIES.PRICE.VALUE) }}
                            />
                          )}
                          <div className="button">
                            <a href={banner.PROPERTIES?.LINK_OBJ?.VALUE || '#'}>{t.detail}</a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Carousel indicators for mobile */}
              {banners.slice(0, 3).length > 1 && (
                <div className="carousel-indicators">
                  {banners.slice(0, 3).map((_, index) => (
                    <span
                      key={index}
                      className={`indicator ${index === currentBannerIndex ? 'active' : ''}`}
                      onClick={() => setCurrentBannerIndex(index)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setCurrentBannerIndex(index);
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Video Banners */}
      <div className="videoimagebanners">
        <div className="container">
          <div className="row">
            <div className="banitems">
              {videoBanners
                .filter((banner) => {
                  // Exclude specific banner IDs - add the ID of the white form/girl image here
                  // Current banners: 180 (mp4), 504 (mp4), 470 (mp4), 516 (mp4), 185 (png), 249 (webp)
                  // Exclude the white form/girl image banner ID here
                  const excludedIds = [
                    // Add the banner ID of the white form/girl image here
                    // For example, if it's ID 185: [185]
                    // Or if it's ID 249: [249]
                    // You can add multiple IDs: [185, 249]
                  ];

                  return !excludedIds.includes(banner.ID);
                })
                .sort((a, b) => {
                  // Sort by SORT field - 500 should be last
                  const aSort = a.SORT || 500;
                  const bSort = b.SORT || 500;
                  // If both are 500, sort by ID
                  if (aSort === 500 && bSort === 500) {
                    return b.ID - a.ID;
                  }
                  // If one is 500, it goes last
                  if (aSort === 500) return 1;
                  if (bSort === 500) return -1;
                  // Otherwise sort by SORT value
                  return aSort - bSort;
                })
                .map((banner) => (
                  <a key={banner.ID} className="banitemlink" href={banner.LINK} target="_blank" rel="noopener noreferrer">
                    <div className="banitem">
                      {banner.IS_VIDEO ? (
                        <video className="baniframe" src={getImageUrl(banner.VIDEO_PATH)} autoPlay muted playsInline loop />
                      ) : (
                        <img src={getImageUrl(banner.VIDEO_PATH)} alt="" loading="lazy" />
                      )}
                    </div>
                  </a>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Countries Block (Restored) */}
      <div id="countriesBlock" className="countriesBlock">
        <div className="container">
          <div className="title">
            <h2>{t.countryname}</h2>
          </div>
          <div className="row">
            <div className="forma">
              <div className="countries">
                {countries.slice(0, 10).map((country) => (
                  <div
                    key={country.ID}
                    className={`counrtyitem${selectedCountryCode === country.CODE_COUNTRY ? ' chosen' : ''}`}
                    data-value={country.CODE_COUNTRY}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectedCountryCode === country.CODE_COUNTRY}
                    onClick={() => handleCountryClick(country)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleCountryClick(country);
                      }
                    }}
                  >
                    <div className="flag-wrapper">
                      {getCountryFlagCode(country.CODE_COUNTRY) ? (
                        <Flag
                          code={getCountryFlagCode(country.CODE_COUNTRY)}
                          size="S"
                        />
                      ) : (
                        <img src="/local/templates/kapussta/img/fil.svg" alt={country.NAME} />
                      )}
                    </div>
                    <p>{country.NAME}</p>
                  </div>
                ))}
                {countries.length > 10 && (
                  <>

                    <div className="hideblock">
                      {countries.slice(10).map((country) => (
                        <div
                          key={country.ID}
                          className={`counrtyitem${selectedCountryCode === country.CODE_COUNTRY ? ' chosen' : ''}`}
                          data-value={country.CODE_COUNTRY}
                          role="button"
                          tabIndex={0}
                          aria-pressed={selectedCountryCode === country.CODE_COUNTRY}
                          onClick={() => handleCountryClick(country)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleCountryClick(country);
                            }
                          }}
                        >
                          <div className="flag-wrapper">
                            {getCountryFlagCode(country.CODE_COUNTRY) ? (
                              <Flag
                                code={getCountryFlagCode(country.CODE_COUNTRY)}
                                size="S"
                              />
                            ) : (
                              <img src="/local/templates/kapussta/img/fil.svg" alt={country.NAME} />
                            )}
                          </div>
                          <p>{country.NAME}</p>
                        </div>
                      ))}
                    </div>
                    <div className="closecont">
                      <p>Скрыть</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Work Block */}
      <div id="workBlock" className="workBlock" ref={workBlockRef}>
        <div className="container">
          <div className="row">

          </div>
          <div className="row work-n-chat">
            <div className="col-8">
              <div className="title">
                <h2>{t.work}</h2>
              </div>

              {jobsLoading && <div className="jobs-status">Загружаем вакансии...</div>}
              {jobsError && <div className="jobs-status error">{jobsError}</div>}
              <div className="work">
                {(() => {
                  const firstGroup = jobs.slice(0, 2);
                  const bigJob = jobs[2];
                  const secondGroup = jobs.slice(3, 5);

                  return (
                    <>
                      {firstGroup.length > 0 && (
                        <div className="small">
                          {firstGroup.map((job) => (
                            <div key={job.ID} className="smallWork" data-aos="fade-up" data-aos-once="true" data-aos-offset="150" data-aos-duration="1000">
                              <div className="image">
                                <img src={getImageUrl(job.PREVIEW_PICTURE_SRC)} alt={job.NAME} loading="lazy" />
                              </div>
                              <div className="content">
                                <div className="title">
                                  <h3>{job.NAME}</h3>
                                </div>
                                <div className="cost" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <img src="/local/templates/kapussta/img/doll.svg" alt="$" style={{ width: '14px', height: '14px' }} />
                                  <p dangerouslySetInnerHTML={{ __html: cleanText(job.PROPERTIES?.PRICE?.VALUE || '') }} style={{ margin: 0 }} />
                                </div>
                                <div className="button">
                                  <Link to={job.DETAIL_PAGE_URL?.replace(/\/$/, '') || `/work/${job.CODE}`}>{t.detail}</Link>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {bigJob && (
                        <div className="big">
                          <div className="bigWork" data-aos="fade-right" data-aos-once="true" data-aos-offset="150" data-aos-duration="1000">
                            <div className="image">
                              <img src={getImageUrl(bigJob.PREVIEW_PICTURE_SRC)} alt={bigJob.NAME} loading="lazy" />
                            </div>
                            <div className="content">
                              <div className="title">
                                <h3>{bigJob.NAME}</h3>
                              </div>
                              <div className="opis">
                                <p dangerouslySetInnerHTML={{ __html: cleanText(bigJob.PREVIEW_TEXT) }} />
                              </div>
                              <div className="bottomBlock">
                                <div className="cost" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <img src="/local/templates/kapussta/img/doll.svg" alt="$" style={{ width: '14px', height: '14px' }} />
                                  <p dangerouslySetInnerHTML={{ __html: cleanText(bigJob.PROPERTIES?.PRICE?.VALUE || '') }} style={{ margin: 0 }} />
                                </div>
                                <div className="button">
                                  <Link to={bigJob.DETAIL_PAGE_URL?.replace(/\/$/, '') || `/work/${bigJob.CODE}`}>{t.detail}</Link>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {secondGroup.length > 0 && (
                        <div className="small">
                          {secondGroup.map((job) => (
                            <div key={job.ID} className="smallWork" data-aos="fade-up" data-aos-once="true" data-aos-offset="150" data-aos-duration="1000">
                              <div className="image">
                                <img src={getImageUrl(job.PREVIEW_PICTURE_SRC)} alt={job.NAME} loading="lazy" />
                              </div>
                              <div className="content">
                                <div className="title">
                                  <h3>{job.NAME}</h3>
                                </div>
                                <div className="cost" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <img src="/local/templates/kapussta/img/doll.svg" alt="$" style={{ width: '14px', height: '14px' }} />
                                  <p dangerouslySetInnerHTML={{ __html: cleanText(job.PROPERTIES?.PRICE?.VALUE || '') }} style={{ margin: 0 }} />
                                </div>
                                <div className="button">
                                  <Link to={job.DETAIL_PAGE_URL?.replace(/\/$/, '') || `/work/${job.CODE}`}>{t.detail}</Link>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="btnAll">
                <Link to={
                  selectedCategory
                    ? `/work?country=${selectedCountryCode || ''}&category=${selectedCategory}`
                    : selectedCountryCode
                      ? `/work?country=${selectedCountryCode}`
                      : '/work'
                }>
                  {t.all}
                </Link>
              </div>
            </div>
            <div className="col-4">
              <div className="title font">
                <h2>KAPUSsTA CHAT</h2>
              </div>
              <div className="chat" id="chatContainer">
                {chatMessages.map((msg) => (
                  <div key={msg.ID} className="message right left" data-aos="fade-up" data-aos-once="true" data-aos-offset="150" data-aos-duration="1000">
                    <p className="mess">
                      <span className="name">{msg.nickname}: </span>
                      <span dangerouslySetInnerHTML={{ __html: cleanText(msg.message) }} />
                    </p>
                  </div>
                ))}
              </div>
              <div className="btnAll">
                <a href="https://t.me/kapusstachat" target="_blank" rel="noopener noreferrer">
                  {t.tg} Перейти в тг-канал
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Find Block */}
      <div id="findBlock" className="findBlock">
        <div className="container">
          <div className="row">
            <div className="title">
              <h3 data-aos="fade-up" data-aos-once="true" data-aos-offset="150" data-aos-duration="1000">
                {t.t3add}
              </h3>
              <h2 data-aos="fade-up" data-aos-once="true" data-aos-offset="150" data-aos-duration="1500">
                {t.t2add}
              </h2>
            </div>
            <div className="opis" data-aos="fade-up" data-aos-once="true" data-aos-offset="150" data-aos-duration="2000">
              <p>{t.conadd}</p>
            </div>
            <div className="button" data-aos="fade-up" data-aos-once="true" data-aos-offset="150" data-aos-duration="2000">
              <Link to={(localStorage.getItem('user') && localStorage.getItem('user') !== 'null') ? "/profile?tab=ads" : "/auth"}>{t.subadd}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;

